import classNames from 'classnames';
import React, {
    ReactElement,
    useState,
    useEffect,
    useMemo,
    FocusEvent,
    ChangeEvent,
} from 'react';

import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';
import { getRandomId } from '../utils/getRandomId';

import { ISelectOption } from './SelectOption';
import { getDefaultSelectedOption, OzSelectInputOptions } from './SelectOptions';

export type ISelectInputProps = IQATestableProps & IStyleableProps & {
    onChange: (option: ISelectOption) => void;
    onBlur?: (event: FocusEvent<HTMLElement>) => void;
    disabled?: boolean;
    error?: string;
    hint?: string;
    label: string;
    options: ISelectOption[];
    selectedValue?: any;
    selectedIndex?: number;
    placeholder?: string;
    name?: string;
};

const emptyOption: ISelectOption = {
    name: '',
    value: '',
};

export function OzSelectInput (props: ISelectInputProps): ReactElement {
    const {
        error,
        hint,
        disabled = false,
        label,
        'data-testid': dataTest = '',
        className = '',
        options,
        selectedValue,
        selectedIndex,
        placeholder,
        onChange,
        onBlur,
        name = '',
    }: ISelectInputProps = props;

    emptyOption.name = placeholder || label;

    const [isFocused, setIsFocused]: [boolean, ISetState<boolean>] = useState<boolean>(false);

    const [optionsWithDefault, setOptionsWithDefault]: [ISelectOption[], ISetState<ISelectOption[]>] =
    useState<ISelectOption[]>([emptyOption, ...options]);

    useEffect((): void => {
        setOptionsWithDefault([emptyOption, ...options]);
    }, [options]);

    const initialOption: ISelectOption = useMemo((): ISelectOption => {
        return getDefaultSelectedOption(optionsWithDefault, selectedValue, selectedIndex);
    }, [optionsWithDefault, selectedValue, selectedIndex]);

    const [selectedOption, setSelectedOption]: [ISelectOption, ISetState<ISelectOption>] = useState<ISelectOption>(initialOption);

    useEffect((): void => {
        onChange(selectedOption);
    }, [selectedOption, onChange]);

    useEffect((): void => {
        const newSelectedOption: ISelectOption = getDefaultSelectedOption(optionsWithDefault, selectedValue, selectedIndex);

        setSelectedOption(newSelectedOption);
    }, [selectedValue, optionsWithDefault, selectedIndex]);

    const wrapperClassName: string = classNames({
        'w-full h-8 flex flex-row justify-center items-center': true,
        'relative border border-gray-300 rounded bg-white-500 outline-none focus:outline-none ': true,
        '!border-red-500': (error && !disabled),
        '!border-teal-500': (isFocused && !error),
        '!bg-gray-100': disabled,
        [className]: !!className,
    });

    const labelClassName: string = classNames({
        'w-full mb-1/2 block text-xs text-left text-gray-700': true,
        '!font-medium truncate opacity-100 leading-4': true,
        'transition-opacity ease-in-out duration-500 pl-2 pr-8': true,
        'hidden flex opacity-00': selectedOption.name === emptyOption.name,
    });

    const emptyOptionClassName: string = classNames({
        'w-full bg-transparent text-med text-left font-medium text-gray-600': true,
        'leading-relaxed outline-none caret-primary pl-2 pr-8 truncate': true,
        'text-secondary-contrast caret-error': (!!error && disabled),
    });

    const valueClassName: string = classNames({
        'w-full bg-transparent text-med text-left font-medium ': true,
        'leading-relaxed outline-none caret-primary pl-2 pr-8 truncate': true,
        'text-gray-900': !disabled,
        'text-gray-600': disabled,
        'text-secondary-contrast caret-error': (!!error && disabled),
    });

    const errorClassName: string = classNames({
        'w-full text-base font-normal truncate leading-12 pt-1/2': true,
        'text-red-500': !!error,
    });

    const hintClassName: string = classNames({
        'w-full text-base font-normal truncate leading-12 pt-1/2': true,
        'text-gray-700': !!hint,
    });

    function handleSelectChange (event: ChangeEvent<HTMLSelectElement>): void {
        const selectedOptionIndex: number = parseInt(event.target.value);
        const currentSelectedOption = optionsWithDefault[selectedOptionIndex];

        setSelectedOption(currentSelectedOption);
    }

    function handleBlur (event: FocusEvent<HTMLElement>): void {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    }

    function handleFocus (): void {
        setIsFocused(true);
    }

    const id: string = name || dataTest || getRandomId();

    return (
        <div className="w-full h-auto">
            <button
                className={wrapperClassName}
                disabled={disabled}
                type='button'
                data-testid={dataTest}
            >
                <div className="w-full align-middle">
                    <label className={labelClassName} htmlFor={id}>{label}</label>
                    {(selectedOption.name === emptyOption.name && selectedOption.value === emptyOption.value) ?
                        <div className={emptyOptionClassName}>{emptyOption.name}</div>
                        :
                        <div className={valueClassName}>{selectedOption.name}</div>
                    }
                </div>

                <div className='absolute right-2 align-middle w-4 h-5 border-l flex items-center justify-end'>
                    <span className='inline-block transform scale-x-125 text-gray-500'>
                        &#x25BC;
                    </span>
                </div>

                <select
                    id={id}
                    className='w-full h-full type-body1 absolute left-0 opacity-00 font-normal'
                    defaultValue={selectedOption.value}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleSelectChange}
                    disabled={disabled}
                    data-testid={!!dataTest && `${dataTest}-select`}
                    name={name}
                >
                    <OzSelectInputOptions options={optionsWithDefault} data-testid={dataTest} />
                </select>
            </button>

            {hint && !error &&
                <p className={hintClassName}>{hint}</p>}
            {error &&
                <p className={errorClassName}>{error}</p>}
        </div>
    );
}
